import { useState } from 'react';
import toast from 'react-hot-toast';

import { Gate } from '../../../global/contexts/auth/auth';
import { usePlansList } from '../../plans/contexts/plans';
import { useClassifyTicket } from '../contexts/tickets';
import { TICKET_CLASSIFICATIONS, isClassified } from '../utils/tickets';

import '../styles/forms.css';

// Clasificar: alguien lo miró y dijo qué es.
//
// NO ES UN CAMPO, ES UNA OPERACIÓN. Podría haber sido un `<select>` que guarda
// solo, y sería más cómodo y mentiría: el backend deja `classified_by` y
// `classified_at` en cada llamada, y la migración 0032 prohíbe un ticket
// clasificado sin autor. Un control que guarda al soltar el ratón hace que
// firmar un veredicto se sienta como cambiar un filtro. Por eso hay un botón,
// y por eso dice «Clasificar» y no «Guardar».
//
// RECLASIFICAR ESTÁ PERMITIDO y el formulario no desaparece después del primer
// veredicto: deja autor y fecha nuevos, y el histórico queda en los eventos.
// Lo que cambia es el botón, que pasa a decir que está pisando algo.
//
// LO QUE NO HACE, y el tier lo pide explícito: no crea incidentes ni tareas.
// Clasificar un ticket como `error` no abre nada. Agrupar y corregir son
// decisiones de otra persona, en otra pantalla.

const NOTE_MAX = 5000;

export default function ClassifyTicketForm({ ticket }) {
    const classify = useClassifyTicket(ticket.id);
    const already = isClassified(ticket);

    const [open, setOpen] = useState(false);
    const [classification, setClassification] = useState(ticket.classification ?? '');
    const [note, setNote] = useState('');
    const [planId, setPlanId] = useState(ticket.plan_id ? String(ticket.plan_id) : '');
    const [error, setError] = useState('');

    const submit = async (e) => {
        e.preventDefault();
        if (!classification) {
            setError('Elegí qué es.');
            return;
        }

        const payload = { classification };
        if (note.trim()) payload.note = note.trim();
        if (planId) payload.plan_id = Number(planId);

        try {
            await classify.mutateAsync(payload);
            toast.success(already ? 'Ticket reclasificado' : 'Ticket clasificado');
            setNote('');
            setOpen(false);
        } catch (err) {
            setError(err.message || 'No se pudo clasificar');
        }
    };

    if (!open) {
        return (
            <button type="button" className="ticket-form__open" onClick={() => setOpen(true)}>
                {already ? 'Reclasificar' : 'Clasificar'}
            </button>
        );
    }

    return (
        <form className="ticket-form" onSubmit={submit}>
            <label className="ticket-form__field">
                <span className="ticket-form__label">Qué es</span>
                <select
                    className="ticket-form__select"
                    value={classification}
                    onChange={e => { setClassification(e.target.value); setError(''); }}
                >
                    <option value="">Elegir…</option>
                    {TICKET_CLASSIFICATIONS.map(item => (
                        <option key={item.key} value={item.key}>{item.label}</option>
                    ))}
                </select>
            </label>

            {/* EL CAMPO DE PLAN SE MONTA, NO SE ESCONDE, y las dos condiciones
                son distintas.

                Que el ticket tenga proyecto es un hecho del dato: sin proyecto
                no hay planes que ofrecer, y el backend rechaza con 422 un plan
                de otro proyecto.

                Que la cuenta pueda leer planes es un permiso, y va en <Gate>
                (TSS vite/05). Importa que sea un montaje y no un `display:none`:
                la consulta de planes vive DENTRO del componente, así que una
                cuenta con `tasks:write` y sin `plans:read` ni siquiera la
                dispara. Si la disparara, el interceptor de api.js levantaría el
                aviso global «No tienes permiso para esta acción» por el solo
                hecho de abrir un ticket — un error que no es de quien lo abrió,
                sobre algo que no pidió. */}
            {ticket.project_id ? (
                <Gate domain="plans:read">
                    <PlanField
                        projectId={ticket.project_id}
                        value={planId}
                        onChange={setPlanId}
                    />
                </Gate>
            ) : null}

            <label className="ticket-form__field ticket-form__field--wide">
                <span className="ticket-form__label">
                    Nota <span className="ticket-form__optional">— opcional</span>
                </span>
                <textarea
                    className="ticket-form__textarea"
                    value={note}
                    maxLength={NOTE_MAX}
                    rows={3}
                    placeholder="Por qué. Lo lee quien venga después."
                    onChange={e => setNote(e.target.value)}
                />
            </label>

            {error && <p className="ticket-form__error">{error}</p>}

            <div className="ticket-form__actions">
                <button type="submit" className="ticket-form__submit" disabled={classify.isPending}>
                    {classify.isPending ? 'Guardando…' : (already ? 'Reclasificar' : 'Clasificar')}
                </button>
                <button
                    type="button"
                    className="ticket-form__cancel"
                    onClick={() => { setOpen(false); setError(''); }}
                >
                    Cancelar
                </button>
            </div>

            {already && (
                <p className="ticket-form__warn">
                    Ya está clasificado como «{ticket.classification}». Volver a clasificarlo
                    reemplaza el veredicto y deja tu nombre y la fecha de ahora; el anterior
                    queda en los eventos.
                </p>
            )}
        </form>
    );
}

// Los planes DEL PROYECTO DEL TICKET. Una sugerencia que requiere desarrollo se
// relaciona con el plan que la tomaría; no crea nada, deja la referencia para
// que quien escriba ese plan sepa de dónde salió el pedido.
//
// Si el proyecto no tiene ninguno, el campo no se dibuja: un selector con una
// sola opción que dice «Ninguno» es una pregunta sin respuestas posibles.
function PlanField({ projectId, value, onChange }) {
    const plans = usePlansList({ projectId, limit: 100 });
    const options = plans.data?.items ?? [];

    if (!options.length) return null;

    return (
        <label className="ticket-form__field">
            <span className="ticket-form__label">
                Plan relacionado <span className="ticket-form__optional">— opcional</span>
            </span>
            <select
                className="ticket-form__select"
                value={value}
                onChange={e => onChange(e.target.value)}
            >
                <option value="">Ninguno</option>
                {options.map(plan => (
                    <option key={plan.id} value={plan.id}>{plan.name}</option>
                ))}
            </select>
        </label>
    );
}
